/**
 * Phase 24.7.4 T3 — shopping list page (/shopping-list/:planId).
 *
 * Renders backend's by_category → items → rows hierarchy. Each row.id is
 * its own toggle target (multiple unit-rows possible per ingredient).
 * Aggregates (total / checked / in-pantry) computed client-side.
 *
 * Optimistic toggle с rollback on error. Share via Web Share API +
 * clipboard fallback (active items only — checked/pantry excluded).
 *
 * Category labels come from backend `category_label` field (server-side
 * Accept-Language-localized) — no client-side category i18n needed.
 */
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Share2, Check, Package } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { BackButton } from "@/components/ui/BackButton";
import { useToast } from "@/stores/toast";
import {
  getShoppingList,
  toggleShoppingListItem,
  type ShoppingListItemEntry,
  type ShoppingListResponse,
  type ShoppingListRow,
  type ToggleField,
} from "@/api/shopping_list";

interface Aggregates {
  total: number;
  actionable: number; // total minus in_pantry
  checked: number;
  inPantry: number;
}

function computeAggregates(data: ShoppingListResponse): Aggregates {
  let total = 0;
  let checked = 0;
  let inPantry = 0;
  for (const cat of data.by_category) {
    for (const item of cat.items) {
      for (const row of item.rows) {
        total += 1;
        if (row.in_pantry) inPantry += 1;
        else if (row.is_checked) checked += 1;
      }
    }
  }
  return {
    total,
    actionable: total - inPantry,
    checked,
    inPantry,
  };
}

function formatShoppingListAsText(
  data: ShoppingListResponse,
  title: string,
): string {
  const lines: string[] = [title, ""];
  for (const cat of data.by_category) {
    const activeItems: Array<{ name: string; row: ShoppingListRow }> = [];
    for (const item of cat.items) {
      for (const row of item.rows) {
        if (!row.in_pantry && !row.is_checked) {
          activeItems.push({ name: item.ingredient_name, row });
        }
      }
    }
    if (activeItems.length === 0) continue;
    lines.push(`${cat.category_label}:`);
    for (const { name, row } of activeItems) {
      lines.push(`  - ${name} ${row.amount}${row.unit}`);
    }
    lines.push("");
  }
  return lines.join("\n");
}

export default function ShoppingList() {
  const { planId } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { show } = useToast();
  const planIdNum = parseInt(planId || "0", 10);

  const listQ = useQuery({
    queryKey: ["shopping-list", planIdNum],
    queryFn: () => getShoppingList(planIdNum),
    enabled: planIdNum > 0,
    staleTime: 30_000,
  });

  const toggleMut = useMutation({
    mutationFn: ({ itemId, field }: { itemId: number; field: ToggleField }) =>
      toggleShoppingListItem(itemId, field),
    onMutate: async ({ itemId, field }) => {
      await qc.cancelQueries({ queryKey: ["shopping-list", planIdNum] });
      const prev = qc.getQueryData<ShoppingListResponse>([
        "shopping-list",
        planIdNum,
      ]);
      if (prev) {
        const next: ShoppingListResponse = {
          ...prev,
          by_category: prev.by_category.map((cat) => ({
            ...cat,
            items: cat.items.map((item) => ({
              ...item,
              rows: item.rows.map((row) =>
                row.id === itemId ? { ...row, [field]: !row[field] } : row,
              ),
            })),
          })),
        };
        qc.setQueryData(["shopping-list", planIdNum], next);
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(["shopping-list", planIdNum], ctx.prev);
      }
      show({ message: t("shopping_list.toggle_failed"), variant: "error" });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["shopping-list", planIdNum] });
    },
  });

  const aggregates = useMemo<Aggregates | null>(
    () => (listQ.data ? computeAggregates(listQ.data) : null),
    [listQ.data],
  );

  async function handleShare() {
    if (!listQ.data) return;
    const text = formatShoppingListAsText(listQ.data, t("shopping_list.title"));

    const fallbackCopy = async () => {
      try {
        await navigator.clipboard.writeText(text);
        show({ message: t("shopping_list.copied"), variant: "success" });
      } catch {
        show({ message: t("shopping_list.copy_failed"), variant: "error" });
      }
    };

    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: t("shopping_list.share_title"),
          text,
        });
      } catch (err: unknown) {
        const name = err instanceof Error ? err.name : "";
        if (name !== "AbortError") {
          await fallbackCopy();
        }
        // AbortError = user cancelled the share sheet — silent
      }
    } else {
      await fallbackCopy();
    }
  }

  if (listQ.isLoading) {
    return (
      <div
        className="mx-auto max-w-3xl space-y-4 p-4"
        data-testid="shopping-list-loading"
      >
        <BackButton />
        <Card>
          <div className="space-y-3">
            <Skeleton className="h-8 w-1/2" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-32 w-full" />
          </div>
        </Card>
      </div>
    );
  }

  if (listQ.isError || !listQ.data || !aggregates) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-4">
        <BackButton />
        <Card>
          <p
            className="text-[13px] text-coral"
            data-testid="shopping-list-error"
          >
            {t("shopping_list.load_failed")}
          </p>
        </Card>
      </div>
    );
  }

  const data = listQ.data;
  const progressPct =
    aggregates.actionable > 0
      ? Math.round((aggregates.checked / aggregates.actionable) * 100)
      : 0;

  return (
    <div
      className="mx-auto max-w-3xl space-y-4 p-4"
      data-testid="shopping-list-page"
    >
      <BackButton />

      {/* Header */}
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-serif text-[24px] font-medium leading-tight text-navy-deep">
              {t("shopping_list.title")}
            </h1>
            <p className="mt-1 text-[12px] text-taupe">
              {t("shopping_list.plan_subtitle", { id: data.plan_id })}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleShare}
            data-testid="share-button"
          >
            <Share2 className="h-4 w-4" strokeWidth={2} />
            {t("shopping_list.share")}
          </Button>
        </div>

        {/* Progress */}
        <div className="mt-4 space-y-1.5" data-testid="progress">
          <div className="flex items-center justify-between text-[12px] text-taupe">
            <span>
              {t("shopping_list.progress", {
                checked: aggregates.checked,
                total: aggregates.actionable,
              })}
            </span>
            <span className="metric-number text-navy-deep">{progressPct}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-cream-border">
            <div
              className="h-full rounded-full bg-amber transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {aggregates.inPantry > 0 ? (
            <div className="text-[11px] text-taupe">
              {t("shopping_list.in_pantry_count", { n: aggregates.inPantry })}
            </div>
          ) : null}
        </div>
      </Card>

      {/* Warnings */}
      {data.warnings.length > 0 ? (
        <Card>
          <ul className="space-y-1 text-[12px] text-amber-dark">
            {data.warnings.map((w, i) => (
              <li key={i} data-testid={`warning-${i}`}>
                {t(w.key, w.params || {})}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/* Categories */}
      <div className="space-y-3">
        {data.by_category.length === 0 ? (
          <Card>
            <p className="text-[13px] text-taupe" data-testid="empty-state">
              {t("shopping_list.empty")}
            </p>
          </Card>
        ) : (
          data.by_category.map((cat) => (
            <Card key={cat.category} data-testid={`category-${cat.category}`}>
              <h2 className="text-[11px] font-medium uppercase tracking-wider text-amber-dark">
                {cat.category_label}
              </h2>
              <ul className="mt-2 space-y-1">
                {cat.items.flatMap((item) =>
                  item.rows.map((row) => (
                    <ShoppingListRowItem
                      key={row.id}
                      item={item}
                      row={row}
                      onToggle={(field) =>
                        toggleMut.mutate({ itemId: row.id, field })
                      }
                    />
                  )),
                )}
              </ul>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

interface RowProps {
  item: ShoppingListItemEntry;
  row: ShoppingListRow;
  onToggle: (field: ToggleField) => void;
}

function ShoppingListRowItem({ item, row, onToggle }: RowProps) {
  const { t } = useTranslation();
  const isMuted = row.is_checked || row.in_pantry;

  return (
    <li
      className={`flex items-center gap-3 py-1 ${isMuted ? "opacity-60" : ""}`}
      data-testid={`row-${row.id}`}
    >
      <button
        type="button"
        onClick={() => onToggle("is_checked")}
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors ${
          row.is_checked
            ? "border-amber bg-amber text-navy-deep"
            : "border-cream-border hover:border-amber/50"
        }`}
        aria-pressed={row.is_checked}
        data-testid={`check-toggle-${row.id}`}
      >
        {row.is_checked ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
      </button>

      <span
        className={`flex-1 text-[13px] text-navy-deep ${
          row.is_checked ? "line-through" : ""
        }`}
      >
        {item.ingredient_name}
      </span>

      <span className="metric-number shrink-0 text-[12px] tabular-nums text-taupe">
        {row.amount} {row.unit}
      </span>

      <button
        type="button"
        onClick={() => onToggle("in_pantry")}
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors ${
          row.in_pantry
            ? "bg-amber/20 text-amber-dark"
            : "text-taupe hover:bg-cream-border/40"
        }`}
        title={t("shopping_list.in_pantry_label")}
        aria-pressed={row.in_pantry}
        data-testid={`pantry-toggle-${row.id}`}
      >
        <Package className="h-3.5 w-3.5" strokeWidth={1.8} />
      </button>
    </li>
  );
}
